import { newId, type UniformItemRow } from '@routeback/core';
import { saveMyProfile } from '../me/profile';
import { Empty, useToast } from '@routeback/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { STATUS_LABEL, batchStatus, batches, validateLines, type Batch, type Line } from './uniforms';
import { useMyProfile, useMyUniformRequests, useUniformItems } from './useUniformData';

/**
 * Driver: ask for uniforms like the Driver Portal's form — each item with a
 * size (remembered on my profile) and a quantity up to the item's limit —
 * plus my past requests. Works with no signal.
 */
export function UniformsView() {
  const app = useApp();
  const toast = useToast();
  const nav = useNavigate();
  const items = useUniformItems();
  const profile = useMyProfile();
  const mine = useMyUniformRequests();
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const [qty, setQty] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);

  // Saved sizes load once the profile is read.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (profile === undefined || loaded) return;
    setSizes(profile?.sizes ?? {});
    setLoaded(true);
  }, [profile, loaded]);

  const lines: Line[] = items.map((i) => ({ itemId: i.id, size: sizes[i.id] ?? '', qty: qty[i.id] ?? 0 }));
  const error = validateLines(lines, items);
  const count = lines.reduce((n, l) => n + l.qty, 0);

  /** Remember sizes on my profile (creates it the first time). */
  const saveSizes = async (next: Record<string, string>) => {
    await saveMyProfile(app, { sizes: Object.fromEntries(Object.entries(next).filter(([, v]) => v.trim())) });
  };
  const setSize = (id: string, v: string) => setSizes((s) => ({ ...s, [id]: v }));
  const step = (it: UniformItemRow, d: number) => setQty((q) => ({ ...q, [it.id]: Math.max(0, Math.min(it.max_qty, (q[it.id] ?? 0) + d)) }));

  const send = async () => {
    setTried(true);
    if (error || !app.active) return;
    setBusy(true);
    const batch = newId();
    for (const l of lines.filter((x) => x.qty > 0)) {
      await app.repos.uniformRequests.save(app.repos.uniformRequests.blank({
        user_id: app.userId, item_id: l.itemId, size: l.size.trim(), qty: l.qty, note: note.trim(), status: 'requested',
        manager_note: null, status_by: null, status_at: null, batch_id: batch,
      }));
    }
    await saveSizes(sizes);
    setBusy(false);
    setQty({});
    setNote('');
    setTried(false);
    toast(navigator.onLine === false ? 'Saved — it will send when you have signal' : 'Sent to your manager');
    nav(`/requests/uniforms/${batch}`);
  };

  const all = batches(mine);
  return (
    <div>
      <div className="settings-head"><Link to="/home" className="btn btn-sm btn-ghost">‹ Home</Link><h2>👕 Uniforms</h2></div>
      {items.length === 0 ? (
        <div className="card"><Empty>Your company hasn't set up uniform items yet.</Empty></div>
      ) : (
        <div className="card">
          <p className="muted small" style={{ marginTop: 0 }}>Your sizes are saved for next time — just set how many you need.</p>
          {items.map((it) => {
            const n = qty[it.id] ?? 0;
            const missingSize = tried && n > 0 && !(sizes[it.id] ?? '').trim();
            return (
              <div key={it.id} className={'uni-item' + (n > 0 ? ' uni-on' : '')}>
                <div className="uni-name">{it.name}<span className="muted small"> · up to {it.max_qty}</span></div>
                <div className="uni-controls">
                  {it.sizes.length ? (
                    <select aria-label={`${it.name} size`} value={sizes[it.id] ?? ''} onChange={(e) => setSize(it.id, e.target.value)} style={missingSize ? { borderColor: 'var(--danger)' } : undefined}>
                      <option value="">Size</option>
                      {it.sizes.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  ) : (
                    <input aria-label={`${it.name} size`} value={sizes[it.id] ?? ''} placeholder={it.size_hint || 'Size'} onChange={(e) => setSize(it.id, e.target.value)} style={missingSize ? { borderColor: 'var(--danger)' } : undefined} />
                  )}
                  <div className="uni-stepper" role="group" aria-label={`${it.name} quantity`}>
                    <button type="button" aria-label={`Fewer ${it.name}`} disabled={n === 0} onClick={() => step(it, -1)}>−</button>
                    <span aria-live="polite">{n}</span>
                    <button type="button" aria-label={`More ${it.name}`} disabled={n >= it.max_qty} onClick={() => step(it, 1)}>+</button>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="field" style={{ marginTop: 12 }}><label htmlFor="uni-note">Notes (optional)</label>
            <textarea id="uni-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything your manager should know" />
          </div>
          {!app.active && <p className="muted small">Your company's subscription isn't active, so requests can't be sent right now.</p>}
          {tried && error && <p className="small" role="alert" style={{ color: 'var(--danger)', textAlign: 'center' }}>{error}</p>}
          <button className="btn btn-primary btn-block" disabled={busy || !app.active || count === 0} onClick={() => void send()}>
            {busy ? 'Saving…' : count ? `Send request (${count} item${count === 1 ? '' : 's'})` : 'Send request'}
          </button>
        </div>
      )}
      <div className="section-title">My requests</div>
      {all.length ? <div className="list">{all.slice(0, 30).map((b) => <UniformBatchItem key={b.id} b={b} />)}</div> : <div className="card"><Empty>No requests yet.</Empty></div>}
    </div>
  );
}

export function UniformBatchItem({ b }: { b: Batch }) {
  const items = useUniformItems(true);
  const name = new Map(items.map((i) => [i.id, i.name]));
  const st = batchStatus(b);
  const summary = b.lines.filter((l) => l.status !== 'cancelled').map((l) => `${name.get(l.item_id ?? '') ?? 'Item'} ${l.size}${l.qty > 1 ? ` ×${l.qty}` : ''}`).join(', ');
  return (
    <Link to={`/requests/uniforms/${b.id}`} className="list-item settings-link">
      <span style={{ width: 26, textAlign: 'center' }}>👕</span>
      <div className="grow">
        <div>Uniforms · {new Date(b.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
        <div className="muted small">{summary || 'Cancelled'}</div>
      </div>
      <span className={'chip' + (st === 'ready' || st === 'delivered' ? ' on' : '')}>{STATUS_LABEL[st]}</span>
    </Link>
  );
}

